import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Trophy, Heart, Medal, Loader2, ChevronRight } from 'lucide-react';
import { getLeaderboard, getStats } from '@/lib/api';

const LeaderboardRow = ({ entry, index, onPress }) => {
  const getRankIcon = () => {
    if (entry.rank === 1) return <Trophy className="w-5 h-5 text-yellow-500" />;
    if (entry.rank === 2) return <Medal className="w-5 h-5 text-gray-400" />;
    if (entry.rank === 3) return <Medal className="w-5 h-5 text-amber-600" />;
    return <span className="w-5 text-center text-[#A1A1AA] text-sm">{entry.rank}</span>;
  };

  return (
    <motion.button
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: Math.min(index * 0.03, 0.5) }}
      onClick={onPress}
      className={`w-full flex items-center gap-3 px-3 py-3.5 ${
        entry.rank <= 3 ? 'bg-[#1D1D21]' : 'bg-[#141416]'
      } border border-[#27272A] ${entry.rank === 1 ? 'border-yellow-500/50' : ''} hover:border-[#E63946]/30 transition-colors text-left`}
      style={{ touchAction: 'manipulation' }}
      data-testid={`leaderboard-row-${entry.rank}`}
    >
      <div className="w-7 flex items-center justify-center flex-shrink-0">
        {getRankIcon()}
      </div>

      <div className="flex-1 min-w-0">
        <span className="text-white text-sm font-medium truncate block">{entry.pseudo}</span>
      </div>

      <div className="flex items-center gap-3 flex-shrink-0">
        <span className="text-white font-bold text-lg tabular-nums">{entry.points}</span>
        <div className="flex items-center gap-1 text-[#E63946]">
          <span className="text-sm font-bold tabular-nums">{entry.hearts_found}</span>
          <Heart className="w-3.5 h-3.5" fill="#E63946" />
        </div>
      </div>

      <ChevronRight className="w-4 h-4 text-[#52525B] flex-shrink-0" />
    </motion.button>
  );
};

const Leaderboard = () => {
  const navigate = useNavigate();
  const [leaderboard, setLeaderboard] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [lbData, statsData] = await Promise.all([
          getLeaderboard(),
          getStats()
        ]);
        setLeaderboard(lbData);
        setStats(statsData);
      } catch (e) {
        console.error('Failed to fetch leaderboard:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-10rem)] flex items-center justify-center" data-testid="leaderboard-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#E63946]" />
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-10rem)] px-4 py-8" data-testid="leaderboard-page">
      <div className="max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-6"
        >
          <Trophy className="w-12 h-12 text-[#E63946] mx-auto mb-4" />
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight uppercase mb-2">
            Top Finders
          </h1>
          {stats && (
            <p className="text-[#A1A1AA] text-sm">
              {stats.total_explorers} Finders ont trouvé {stats.total_discovered} cœurs
            </p>
          )}
        </motion.div>

        {/* Column headers */}
        {leaderboard.length > 0 && (
          <div className="flex items-center gap-3 px-3 mb-2 text-[10px] uppercase tracking-widest text-[#52525B]">
            <div className="w-7" />
            <div className="flex-1">Pseudo</div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <span>Points</span>
              <span className="w-12 text-right">Cœurs</span>
            </div>
            <div className="w-4" />
          </div>
        )}

        {leaderboard.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <Heart className="w-16 h-16 text-[#27272A] mx-auto mb-4" />
            <h2 className="text-xl font-display font-bold mb-2">Aucun Finder</h2>
            <p className="text-[#A1A1AA]">Sois le premier à apparaître ici!</p>
          </motion.div>
        ) : (
          <div className="space-y-1.5">
            {leaderboard.map((entry, index) => (
              <LeaderboardRow
                key={`${entry.pseudo}-${entry.rank}`}
                entry={entry}
                index={index}
                onPress={() => entry.device_id && navigate(`/explorer/${entry.device_id}`)}
              />
            ))}
          </div>
        )}

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-6 text-center"
        >
          <p className="text-xs text-[#52525B]">Points = somme des numéros de cœurs trouvés</p>
        </motion.div>
      </div>
    </div>
  );
};

export default Leaderboard;
